import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';

type errorKeys =
	| 'driverFirstName'
	| 'driverLastName'
	| 'driverPhone'
	| 'cargoIdentification'
	| 'selectedLengthTypeId'
	| 'hiredVehicle'
	| 'disabled'
	| 'cargoTypeId'
	| 'cargoMake'
	| 'cargoModel';

export interface ICargoEntityAttributes extends IModelAttributes {
	cargoIdentification: string;
	tbc: boolean;
	disabled: boolean;
	legacyId: number;

	cargoDetails: Array<Models.CargoDetailEntity | Models.ICargoDetailEntityAttributes>;
	cargoTypeId: string;
	cargoType: Models.CargoTypeEntity | Models.ICargoTypeEntityAttributes;
	userId: string;
	user: Models.UserEntity | Models.IUserEntityAttributes;
	selectedWidthId?: string;
	selectedWidth?: Models.WidthEntity | Models.IWidthEntityAttributes;
	selectedLengthId: string;
	selectedLength: Models.MeasurementEntity | Models.IMeasurementEntityAttributes;
	selectedWeightId: string;
	selectedWeight: Models.MeasurementEntity | Models.IMeasurementEntityAttributes;
	exactWeight?: number | null;
	exactWeightUnit?: string | null;
}
@entity('CargoEntity', 'Cargo')
export default class CargoEntity extends Model implements ICargoEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	/**
	 * The identification for the cargo item. E.g. rego.
	 */
	@observable
	@attribute<CargoEntity, string>()
	public cargoIdentification: string;

	/**
	 * If the cargo identification is to be confirmed later.
	 */
	@observable
	@attribute<CargoEntity, boolean>()
	public tbc: boolean;

	@observable
	@attribute<CargoEntity, boolean>()
	public disabled: boolean;

	@observable
	@attribute<CargoEntity, number>()
	public legacyId: number;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public cargoDetails: Models.CargoDetailEntity[] = [];

	@observable
	@attribute()
	public cargoTypeId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public cargoType: Models.CargoTypeEntity;

	@observable
	@attribute()
	public userId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public user: Models.UserEntity;

	@observable
	@attribute()
	public selectedWidthId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public selectedWidth: Models.WidthEntity;

	@observable
	@attribute()
	public selectedLengthId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public selectedLength: Models.MeasurementEntity;

	@observable
	@attribute()
	public selectedWeightId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public selectedWeight: Models.MeasurementEntity;

	@observable
	@attribute<CargoEntity, number | null>()
	public exactWeight: number | null;

	@observable
	@attribute<CargoEntity, string | null>()
	public exactWeightUnit: string | null;

	public errors: { [K in errorKeys]?: string } = {};

	public validateField = async (field: errorKeys): Promise<string | undefined> => {
		switch (field) {
			case 'cargoIdentification': {
				if (!this.cargoIdentification) {
					this.errors.cargoIdentification = 'Required field';
				} else {
					delete this.errors.cargoIdentification;
				}
				break;
			}
			case 'cargoTypeId': {
				if (!this.cargoTypeId) {
					this.errors.cargoTypeId = 'Required field';
				} else {
					delete this.errors.cargoTypeId;
				}
				break;
			}
			case 'cargoMake': {
				if (!this.cargoTypeId) {
					this.errors.cargoMake = 'Required field';
				} else {
					delete this.errors.cargoMake;
				}
				break;
			}
			case 'cargoModel': {
				if (!this.cargoTypeId) {
					this.errors.cargoModel = 'Required field';
				} else {
					delete this.errors.cargoModel;
				}
				break;
			}
		}

		return this.errors[field];
	};

	constructor(attributes?: Partial<ICargoEntityAttributes>) {
		super(attributes);

		this.cargoDetails = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<ICargoEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.cargoIdentification !== undefined) {
				this.cargoIdentification = attributes.cargoIdentification;
			}
			if (attributes.tbc !== undefined) {
				this.tbc = attributes.tbc;
			}
			if (attributes.disabled !== undefined) {
				this.disabled = attributes.disabled;
			}
			if (attributes.legacyId !== undefined) {
				this.legacyId = attributes.legacyId;
			}
			if (attributes.cargoDetails !== undefined && Array.isArray(attributes.cargoDetails)) {
				if (!this.cargoDetails) {
					this.cargoDetails = [];
				}
				for (const model of attributes.cargoDetails) {
					if (model instanceof Models.CargoDetailEntity) {
						this.cargoDetails.push(model);
					} else {
						this.cargoDetails.push(new Models.CargoDetailEntity(model));
					}
				}
			}
			if (attributes.cargoTypeId !== undefined) {
				this.cargoTypeId = attributes.cargoTypeId;
			}
			if (attributes.cargoType !== undefined) {
				if (attributes.cargoType === null) {
					this.cargoType = attributes.cargoType;
				} else if (attributes.cargoType instanceof Models.CargoTypeEntity) {
					this.cargoType = attributes.cargoType;
					this.cargoTypeId = attributes.cargoType.id;
				} else {
					this.cargoType = new Models.CargoTypeEntity(attributes.cargoType);
					this.cargoTypeId = this.cargoType.id;
				}
			}
			if (attributes.userId !== undefined) {
				this.userId = attributes.userId;
			}
			if (attributes.user !== undefined) {
				if (attributes.user === null) {
					this.user = attributes.user;
				} else if (attributes.user instanceof Models.UserEntity) {
					this.user = attributes.user;
					this.userId = attributes.user.id;
				} else {
					this.user = new Models.UserEntity(attributes.user);
					this.userId = this.user.id;
				}
			}
			if (attributes.selectedWidthId !== undefined) {
				this.selectedWidthId = attributes.selectedWidthId;
			}
			if (attributes.selectedWidth !== undefined) {
				if (attributes.selectedWidth === null) {
					this.selectedWidth = attributes.selectedWidth;
				} else if (attributes.selectedWidth instanceof Models.WidthEntity) {
					this.selectedWidth = attributes.selectedWidth;
					this.selectedWidthId = attributes.selectedWidth.id;
				} else {
					this.selectedWidth = new Models.WidthEntity(attributes.selectedWidth);
					this.selectedWidthId = this.selectedWidth.id;
				}
			}
			if (attributes.selectedLengthId !== undefined) {
				this.selectedLengthId = attributes.selectedLengthId;
			}
			if (attributes.selectedLength !== undefined) {
				if (attributes.selectedLength === null) {
					this.selectedLength = attributes.selectedLength;
				} else if (attributes.selectedLength instanceof Models.MeasurementEntity) {
					this.selectedLength = attributes.selectedLength;
					this.selectedLengthId = attributes.selectedLength.id;
				} else {
					this.selectedLength = new Models.MeasurementEntity(attributes.selectedLength);
					this.selectedLengthId = this.selectedLength.id;
				}
			}
			if (attributes.selectedWeightId !== undefined) {
				this.selectedWeightId = attributes.selectedWeightId;
			}
			if (attributes.selectedWeight !== undefined) {
				if (attributes.selectedWeight === null) {
					this.selectedWeight = attributes.selectedWeight;
				} else if (attributes.selectedWeight instanceof Models.MeasurementEntity) {
					this.selectedWeight = attributes.selectedWeight;
					this.selectedWeightId = attributes.selectedWeight.id;
				} else {
					this.selectedWeight = new Models.MeasurementEntity(attributes.selectedWeight);
					this.selectedWeightId = this.selectedWeight.id;
				}
			}
			if (attributes.exactWeight !== undefined) {
				this.exactWeight = attributes.exactWeight;
			}
			if (attributes.exactWeightUnit !== undefined) {
				this.exactWeightUnit = attributes.exactWeightUnit;
			}
		}
	}
}
